import { isAddress } from "viem";
import { base, baseSepolia, mainnet, sepolia } from "viem/chains";

export type HexAddress = `0x${string}`;

export const ZERO_ADDRESS: HexAddress = "0x0000000000000000000000000000000000000000";

export const ADDRESS_ENV = {
  nft: "VITE_CODE_APE_NFT_ADDRESS",
  claimVault: "VITE_CLAIM_VAULT_ADDRESS",
  hook: "VITE_CODE_APE_HOOK_ADDRESS",
  capeToken: "VITE_CAPE_CONTRACT_ADDRESS",
  liquidityVault: "VITE_LIQUIDITY_VAULT_ADDRESS",
  lpCommunityVault: "VITE_LP_COMMUNITY_VAULT_ADDRESS",
  referralRewardVault: "VITE_REFERRAL_REWARD_VAULT_ADDRESS"
} as const;

export function envString(envName: string): string | null {
  const value = import.meta.env[envName] as string | undefined;
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed || null;
}

export function envPositiveInteger(envName: string, fallback: number): number {
  const raw = envString(envName);
  if (!raw) return fallback;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
}

export const EXPECTED_CHAIN_ID = Number(envString("VITE_CHAIN_ID") ?? String(base.id));
export const EXPECTED_CHAIN = EXPECTED_CHAIN_ID === base.id ? base : EXPECTED_CHAIN_ID === baseSepolia.id ? baseSepolia : EXPECTED_CHAIN_ID === sepolia.id ? sepolia : mainnet;
export const EXPECTED_CHAIN_LABEL = EXPECTED_CHAIN_ID === base.id ? "Base" : EXPECTED_CHAIN_ID === baseSepolia.id ? "Base Sepolia" : EXPECTED_CHAIN_ID === sepolia.id ? "Sepolia" : "Ethereum Mainnet";
export const EXPECTED_CHAIN_ID_BIGINT = BigInt(EXPECTED_CHAIN.id);
export const EXPECTED_CHAIN_ID_HEX = `0x${EXPECTED_CHAIN.id.toString(16)}`;
export const TOTAL_SUPPLY_CAP = envPositiveInteger("VITE_TOTAL_SUPPLY_CAP", 2100);
export const MAX_MINT_QUANTITY = envPositiveInteger("VITE_MAX_MINT_QUANTITY", 5);
export const MINT_PRICE_ETH = envString("VITE_MINT_PRICE_ETH") ?? "0.005";
export const MINT_PRICE_WEI = envString("VITE_MINT_PRICE_WEI") ?? "5000000000000000";
export const REFERRAL_REWARDS_ONCHAIN_ENABLED = envString("VITE_REFERRAL_REWARDS_ONCHAIN_ENABLED") === "true";

export function envAddress(envName: string): HexAddress | null {
  const value = envString(envName);
  if (value && isAddress(value) && value.toLowerCase() !== ZERO_ADDRESS) return value as HexAddress;
  return null;
}

export function requireEnvAddress(envName: string): HexAddress {
  const value = envAddress(envName);
  if (!value) {
    throw new Error(`${envName} is not configured. This UI is not ready for contract writes.`);
  }
  return value;
}

export function hasAllRequiredContractAddresses(): boolean {
  return Boolean(
    envAddress(ADDRESS_ENV.nft) &&
    envAddress(ADDRESS_ENV.claimVault) &&
    envAddress(ADDRESS_ENV.hook)
  );
}
